<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import SidePanel from 'dashboard/components-next/side-panel/SidePanel.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { TemplatePreview } from 'dashboard/components-next/template-preview';
import { PLATFORMS } from 'dashboard/services/TemplateConstants';
import InboxesAPI from 'dashboard/api/inboxes';
import { useAlert } from 'dashboard/composables';
import { formatTemplateLanguage } from './templateUtils';
import {
  MAX_QUICK_REPLIES,
  TEMPLATE_CATEGORIES,
  TEMPLATE_LANGUAGES,
  buildPreviewTemplate,
  buildTemplatePayload,
  extractDuplicateDraft,
  validateTemplateForm,
} from './templateCreateUtils';

const props = defineProps({
  inboxes: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['created']);
const { t } = useI18n();

const panelRef = ref(null);
const isSubmitting = ref(false);
const touched = ref(false);
const serverError = ref('');

const emptyForm = () => ({
  inboxId: '',
  name: '',
  category: 'MARKETING',
  language: 'pt_BR',
  body: '',
  quickReplies: [],
});

const form = ref(emptyForm());

const inboxOptions = computed(() =>
  props.inboxes.map(inbox => ({ value: String(inbox.id), label: inbox.name }))
);

const categoryOptions = computed(() =>
  TEMPLATE_CATEGORIES.map(category => ({
    value: category,
    label: t(`WHATSAPP_TEMPLATE_MGMT.CREATE.CATEGORIES.${category}`),
  }))
);

const languageOptions = computed(() =>
  TEMPLATE_LANGUAGES.map(language => ({
    value: language,
    label: formatTemplateLanguage(language),
  }))
);

const errors = computed(() =>
  touched.value ? validateTemplateForm(form.value) : {}
);

const isValid = computed(
  () => Object.keys(validateTemplateForm(form.value)).length === 0
);

const previewTemplate = computed(() => buildPreviewTemplate(form.value));

const resetForm = () => {
  form.value = emptyForm();
  touched.value = false;
  serverError.value = '';
};

const open = () => {
  resetForm();
  panelRef.value?.open();
};

const openAsDuplicate = template => {
  const draft = extractDuplicateDraft(template);
  form.value = { ...emptyForm(), ...draft };
  touched.value = false;
  serverError.value = '';
  panelRef.value?.open();
};

const addQuickReply = () => {
  if (form.value.quickReplies.length >= MAX_QUICK_REPLIES) return;
  form.value.quickReplies.push('');
};

const removeQuickReply = index => {
  form.value.quickReplies.splice(index, 1);
};

const submit = async () => {
  touched.value = true;
  serverError.value = '';
  if (!isValid.value || !form.value.inboxId) return;

  isSubmitting.value = true;
  try {
    await InboxesAPI.createMessageTemplate(
      form.value.inboxId,
      buildTemplatePayload(form.value)
    );
    useAlert(t('WHATSAPP_TEMPLATE_MGMT.CREATE.SUCCESS'));
    emit('created');
    panelRef.value?.close();
    resetForm();
  } catch (error) {
    const responseError = error?.response?.data?.error;
    const details = error?.response?.data?.details;
    serverError.value =
      responseError || t('WHATSAPP_TEMPLATE_MGMT.CREATE.SUBMIT_ERROR');
    if (details) {
      serverError.value += ` (${JSON.stringify(details)})`;
    }
    useAlert(serverError.value);
  } finally {
    isSubmitting.value = false;
  }
};

defineExpose({ open, openAsDuplicate, close: () => panelRef.value?.close() });
</script>

<template>
  <SidePanel
    ref="panelRef"
    width="lg"
    :title="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.TITLE')"
    :description="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.DESCRIPTION')"
    @close="resetForm"
  >
    <div class="flex flex-col gap-4">
      <label class="flex flex-col gap-1.5 text-heading-3 text-n-slate-12">
        {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.INBOX') }}
        <Select
          v-model="form.inboxId"
          class="!w-full [&>select]:w-full"
          :options="inboxOptions"
          :placeholder="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.INBOX_PLACEHOLDER')"
        />
      </label>

      <Input
        v-model="form.name"
        :label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.NAME')"
        :placeholder="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.NAME_PLACEHOLDER')"
        :message="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.NAME_HINT')"
        :message-type="errors.name ? 'error' : 'info'"
        autocomplete="off"
      />

      <div class="grid grid-cols-2 gap-4">
        <label class="flex flex-col gap-1.5 text-heading-3 text-n-slate-12">
          {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.CATEGORY') }}
          <Select
            v-model="form.category"
            class="!w-full [&>select]:w-full"
            :options="categoryOptions"
          />
        </label>
        <label class="flex flex-col gap-1.5 text-heading-3 text-n-slate-12">
          {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.LANGUAGE') }}
          <Select
            v-model="form.language"
            class="!w-full [&>select]:w-full"
            :options="languageOptions"
          />
        </label>
      </div>

      <TextArea
        v-model="form.body"
        :label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.BODY')"
        :placeholder="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.BODY_PLACEHOLDER')"
        :message="
          errors.body === 'variables'
            ? $t('WHATSAPP_TEMPLATE_MGMT.CREATE.BODY_VARIABLES')
            : errors.body
              ? $t('WHATSAPP_TEMPLATE_MGMT.CREATE.REQUIRED')
              : $t('WHATSAPP_TEMPLATE_MGMT.CREATE.BODY_HINT')
        "
        :message-type="errors.body ? 'error' : 'info'"
        auto-height
      />

      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <span class="text-heading-3 text-n-slate-12">
            {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.QUICK_REPLIES') }}
          </span>
          <Button
            v-if="form.quickReplies.length < MAX_QUICK_REPLIES"
            color="slate"
            size="sm"
            icon="i-lucide-plus"
            :label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.ADD_REPLY')"
            @click="addQuickReply"
          />
        </div>
        <div
          v-for="(_, index) in form.quickReplies"
          :key="index"
          class="flex items-center gap-2"
        >
          <Input
            v-model="form.quickReplies[index]"
            class="flex-1"
            :placeholder="
              $t('WHATSAPP_TEMPLATE_MGMT.CREATE.QUICK_REPLY_PLACEHOLDER')
            "
          />
          <Button
            color="slate"
            size="sm"
            icon="i-lucide-x"
            :aria-label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.REMOVE_REPLY')"
            @click="removeQuickReply(index)"
          />
        </div>
        <p v-if="errors.quickReplies" class="text-label-small text-n-ruby-11">
          {{
            $t(
              `WHATSAPP_TEMPLATE_MGMT.CREATE.QUICK_REPLIES_${errors.quickReplies.toUpperCase()}`
            )
          }}
        </p>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-heading-3 text-n-slate-12">
          {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.PREVIEW_TITLE') }}
        </span>
        <div
          class="flex items-center justify-center px-6 py-8 border rounded-xl min-h-60 border-n-weak bg-n-alpha-1"
        >
          <TemplatePreview
            :template="previewTemplate"
            :platform="PLATFORMS.WHATSAPP"
          />
        </div>
      </div>

      <p v-if="serverError" class="text-sm text-n-ruby-11">
        {{ serverError }}
      </p>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          color="slate"
          :label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.CANCEL')"
          :disabled="isSubmitting"
          @click="panelRef?.close()"
        />
        <Button
          :label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.SUBMIT')"
          icon="i-lucide-send"
          :is-loading="isSubmitting"
          :disabled="isSubmitting || !form.inboxId"
          @click="submit"
        />
      </div>
    </template>
  </SidePanel>
</template>
